'use client';

import { useEffect, useRef } from 'react';
import { useNotificationsStore } from '@/stores/notifications';
import { useAuthStore } from '@/stores/auth';
import { getFirebaseConfig, initializeMessaging, getFCMToken, isFCMConfigured } from '@/lib/firebase';
import { FCM_ANONYMOUS_ID_KEY, registerGuestToken } from '@/lib/fcm-utils';

/**
 * Hook to initialize Firebase Cloud Messaging
 * Call this in your root layout or app component
 * 
 * This hook:
 * - Registers the service worker for background notifications
 * - Initializes FCM when user is logged in
 * - Fetches notifications from API on login
 * - Cleans up on logout
 */
export function useFCM() {
  const { initializeFCM, fcmInitialized, unregisterFCMToken, fetchNotifications, setUnreadCount } = useNotificationsStore();
  const { isLoggedIn, authLoading, user } = useAuthStore();
  const notificationsEnabled = user?.notifications_enabled !== false;

  // Setup multi-tab sync via storage events
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (event: StorageEvent) => {
      if (!event.key || !event.newValue) {
        // Handle clear events
        if (event.key === 'notifications_unread_count' && event.newValue === null) {
          setUnreadCount(0);
        } else if (event.key === 'notifications_pending_count' && event.newValue === null) {
          const { setPendingCount } = useNotificationsStore.getState();
          setPendingCount(0);
        }
        return;
      }
      
      if (event.key === 'notifications_unread_count') {
        try {
          const cached = JSON.parse(event.newValue) as { count: number; lastFetched: number };
          setUnreadCount(cached.count);
        } catch {
          // Ignore parse errors
        }
      } else if (event.key === 'notifications_pending_count') {
        try {
          const cached = JSON.parse(event.newValue) as { count: number; lastFetched: number };
          const { setPendingCount } = useNotificationsStore.getState();
          setPendingCount(cached.count);
        } catch {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [setUnreadCount]);

  // Register service worker for background notifications (always register so it appears in F12; send config when Firebase is set)
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/',
        });

        const firebaseConfig = getFirebaseConfig();
        const hasConfig = !!(firebaseConfig.apiKey && firebaseConfig.projectId);

        const sendConfig = (target: ServiceWorker | null) => {
          if (target && hasConfig) {
            target.postMessage({
              type: 'FIREBASE_CONFIG',
              config: firebaseConfig,
            });
          }
        };

        if (registration.installing) {
          registration.installing.addEventListener('statechange', () => {
            if (registration.installing?.state === 'activated' && registration.active) {
              sendConfig(registration.active);
            }
          });
        } else if (registration.active) {
          sendConfig(registration.active);
        } else if (registration.waiting) {
          sendConfig(registration.waiting);
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('[FCM] Service Worker registered:', registration.scope, hasConfig ? '(config sent)' : '(no Firebase config yet)');
        }
      } catch (error) {
        console.error('[FCM] Service Worker registration failed:', error);
      }
    };

    registerServiceWorker();
  }, []);

  // Listen for service worker messages (NOTIFICATION_UPDATE)
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data && 
          event.data.type === 'NOTIFICATION_UPDATE' && 
          event.data.source === 'firebase-messaging-sw') {
        if (process.env.NODE_ENV === 'development') {
          console.log('[useFCM] Received NOTIFICATION_UPDATE from service worker');
        }
        
        // If count is provided in message, update directly; otherwise fetch
        if (event.data.count !== undefined) {
          const count = parseInt(event.data.count, 10) || 0;
          setUnreadCount(count);
        } else {
          // Force fetch to get fresh data
          fetchNotifications(true);
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [fetchNotifications, setUnreadCount]);

  // Initialize FCM and fetch notifications when user is logged in (and notifications enabled)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (authLoading) return;

    if (isLoggedIn) {
      fetchNotifications(false);

      if (!fcmInitialized && notificationsEnabled) {
        initializeFCM();
      }
    }

    if (!isLoggedIn && fcmInitialized) {
      unregisterFCMToken();
    }
  }, [isLoggedIn, authLoading, fcmInitialized, notificationsEnabled, initializeFCM, unregisterFCMToken, fetchNotifications]);

  // Guest FCM: when not logged in and permission already granted, register token as guest and store anonymous_id
  const guestRegisteredRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || isLoggedIn || authLoading) return;
    if (typeof Notification === 'undefined' || !isFCMConfigured() || Notification.permission !== 'granted') return;
    if (guestRegisteredRef.current) return;

    const run = async () => {
      const messaging = initializeMessaging();
      if (!messaging) return;
      const token = await getFCMToken(messaging);
      if (!token) return;
      const existingId = localStorage.getItem(FCM_ANONYMOUS_ID_KEY) ?? undefined;
      const result = await registerGuestToken(token, { anonymous_id: existingId });
      if (result?.anonymous_id) {
        try {
          localStorage.setItem(FCM_ANONYMOUS_ID_KEY, result.anonymous_id);
        } catch {
          // ignore
        }
        guestRegisteredRef.current = true;
      }
    };
    run();
  }, [isLoggedIn, authLoading]);
}

